import React, { useMemo, useState } from 'react';

import {
  DEFAULT_FILTER_VALUE,
  FILTER_DAYS_MAP,
  FILTER_OPTIONS,
  FILTER_OPTIONS_MAP,
  GRAPH_RESPONSIVE_CONFIG,
  Y_AXIS_TICKS
} from 'constants/studentGoals';
import { useStudentGoalSubmissionsDetailGet } from 'sdk';

import StatusUpdateModal from 'pages/Teachers/StudentDetail/components/StatusUpdateModal';
import { convertDaysToMilliseconds } from 'utils/date';
import {
  calculateGraphPoints,
  calculateGridTicks,
  computeSvgLines
} from 'utils/students/goals';

import styles from './styles.module.scss';

const PerformanceGraph = ({
  goalId,
  studentId,
  sectionId,
  isExpanded,
  submissionDetails,
  setSubmissionPeriodFilter,
  trackEvent
}) => {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [activeFilter, setActiveFilter] = useState(DEFAULT_FILTER_VALUE);

  const { data: goalsGraphDetails } = useStudentGoalSubmissionsDetailGet({
    goalId,
    sectionId,
    period: FILTER_OPTIONS_MAP[activeFilter],
    isExpanded: isExpanded && !submissionDetails
  });

  const { minTime, maxTime } = useMemo(() => {
    const currentMax = new Date().getTime();
    const days = FILTER_DAYS_MAP[activeFilter] || 30;
    const currentMin = currentMax - convertDaysToMilliseconds({ days });

    return { minTime: currentMin, maxTime: currentMax };
  }, [activeFilter]);

  const gridTicks = useMemo(() => {
    return calculateGridTicks({ minTime, maxTime });
  }, [minTime, maxTime]);

  const graphPoints = useMemo(() => {
    return calculateGraphPoints({
      records: submissionDetails || goalsGraphDetails || [],
      styleModule: styles,
      minTime,
      maxTime
    });
  }, [goalsGraphDetails, maxTime, minTime, submissionDetails]);

  const svgLines = useMemo(
    () => computeSvgLines({ graphPoints }),
    [graphPoints]
  );

  const graphWidthStyle = useMemo(() => {
    const pointCount = graphPoints.length;

    const config = GRAPH_RESPONSIVE_CONFIG[activeFilter];

    if (config && pointCount > config.pointThreshold) {
      return {
        width: `${pointCount * config.multiplierPx}px`,
        minWidth: '100%'
      };
    }

    return { width: '100%' };
  }, [graphPoints.length, activeFilter]);

  const handleFilterChange = ({ filter }) => {
    setActiveFilter(filter);
    if (setSubmissionPeriodFilter) {
      setSubmissionPeriodFilter(filter);
    }

    trackEvent('time_range_selected', {
      period: filter,
      goal_id: goalId,
      student_id: studentId
    });
  };

  const handleUpdateStatus = ({ newStatus }) => {
    void newStatus;
    setSelectedPoint(null);
  };

  // TODO: Drop in `Enable chaning the goal status`
  const SHOW_POPUP = false;

  return (
    <div className={styles.graphContainer}>
      <div className={styles.graphHeader}>
        <div className={styles.filterButtonGroup}>
          {FILTER_OPTIONS.map((filter) => (
            <button
              key={filter}
              className={`${styles.filterButton} ${
                activeFilter === filter ? styles.activeFilter : ''
              }`}
              onClick={() => handleFilterChange({ filter })}
            >
              {filter}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.scrollWrapper}>
        <div className={styles.scrollTrack} style={graphWidthStyle}>
          <div className={styles.graphContentWrapper}>
            <div className={styles.yAxis}>
              {Y_AXIS_TICKS.map((tick) => (
                <span key={tick} className={styles.yLabel}>
                  {tick}
                </span>
              ))}
            </div>

            <div className={styles.gridDisplay}>
              <div className={styles.horizontalLinesContainer}>
                {Y_AXIS_TICKS.map((tick, index) => (
                  <div
                    key={`grid-h-${index}`}
                    className={styles.horizontalLine}
                  />
                ))}
              </div>

              <div className={styles.verticalLinesContainer}>
                {gridTicks.map((tick, index) => (
                  <div
                    key={`grid-v-${index}`}
                    className={styles.verticalLine}
                    style={{ left: `${tick.x}%` }}
                  />
                ))}
              </div>

              <svg
                className={styles.svgLayer}
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {svgLines.map((line) => (
                  <line
                    key={line.id}
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    stroke={line.stroke}
                    strokeWidth="0.8"
                    strokeDasharray="2,2"
                  />
                ))}
              </svg>

              {graphPoints.map((point, index) => (
                <div
                  key={`dot-${index}`}
                  className={`${styles.graphDot} ${point.color} ${
                    selectedPoint &&
                    selectedPoint.x === point.x &&
                    selectedPoint.y === point.y
                      ? styles.selected
                      : ''
                  }`}
                  style={{
                    left: `${point.x}%`,
                    bottom: `${point.y}%`
                  }}
                  onClick={() => setSelectedPoint(point)}
                />
              ))}
            </div>
          </div>

          <div className={styles.xAxisRow}>
            <div className={styles.yAxisSpacer} />
            <div className={styles.xAxis}>
              {gridTicks.map((tick, index) => (
                <span
                  key={`label-x-${index}`}
                  className={styles.xLabel}
                  style={{ left: `${tick.x}%` }}
                >
                  {tick.dateLabel}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {selectedPoint && SHOW_POPUP && (
        <StatusUpdateModal
          point={selectedPoint}
          onClose={() => setSelectedPoint(null)}
          onUpdateStatus={handleUpdateStatus}
        />
      )}
    </div>
  );
};

export default PerformanceGraph;
